/**
 * 取选型产品代码（用于与 dqProduct.basicMotorCode 比对）。
 *
 * GetInventory 原始返回约定：
 * - jinBasicMotorCode 有值 → 取 jinBasicMotorCode（境界产品有权限时的选型码）
 * - jinBasicMotorCode 无值 → 取 basicMotorCode（无权限 / 非境界）
 *
 * inventoryStore.processInventoryProducts 会 swapJinProductFields，字段对调后
 * 原始 jin↔basic 互换，故已对调数据用 basicMotorCode || jinBasicMotorCode，与上面规则等价。
 *
 * @param fieldsSwapped 是否已对调；inventoryInfo / 面价单中的产品传 true
 */
export const getLectoTypeSelectionProductCode = (
  product: {
    basicMotorCode?: string | null;
    jinBasicMotorCode?: string | null;
  } | null | undefined,
  fieldsSwapped = true,
): string | undefined => {
  if (!product) {
    return undefined;
  }
  if (fieldsSwapped) {
    // 对调后：原始 jinBasicMotorCode 已在 basicMotorCode
    return product.basicMotorCode || product.jinBasicMotorCode || undefined;
  }
  // 接口原始返回：有 jin 取 jin，否则取 basic
  return product.jinBasicMotorCode || product.basicMotorCode || undefined;
};

/** 选型主机代码与国产报价主机代码是否一致（inventory 产品默认已 swap） */
export const isDqBasicMotorCodeMatched = (
  product: {
    basicMotorCode?: string | null;
    jinBasicMotorCode?: string | null;
  } | null | undefined,
  dqProduct: { basicMotorCode?: string | null } | null | undefined,
  fieldsSwapped = true,
): boolean => {
  const lectoTypeCode = getLectoTypeSelectionProductCode(product, fieldsSwapped);
  const dqCode = dqProduct?.basicMotorCode;
  if (!lectoTypeCode || !dqCode) {
    return false;
  }
  return String(lectoTypeCode) === String(dqCode);
};

/** 面价匹配结果是否需要人工报价（与国产报价 isVcNeedQuatation 一致） */
export const isMotorUnitPriceNeedQuotation = (
  unitPriceResult: string | null | undefined,
): boolean => unitPriceResult === 'NULL' || unitPriceResult == null || unitPriceResult === 'R';

/**
 * CreateDq 提交用电机面价。
 * product 须已经 processInventoryProducts：境界产品 unitPrice 已与 jinUnitPrice 对调。
 *
 * - 系统已匹配：一律用选型 unitPrice（境界面价），避免表单/旧 dqProduct 带入标准码面价
 * - 需人工报价：优先表单编辑值，其次主机码一致的已保存国产报价面价
 */
export const resolveMotorUnitPriceForDq = (
  product: {
    unitPrice?: any;
    unitPriceResult?: string | null;
    dqProduct?: {
      unitPrice?: any;
      unitPriceResult?: string | null;
      basicMotorCode?: string | null;
    } | null;
    basicMotorCode?: string | null;
    jinBasicMotorCode?: string | null;
  },
  options?: {
    formUnitPrice?: any;
    savedUnitPrice?: any;
    savedUnitPriceResult?: string | null;
    /** 用于主机码比对的国产报价产品（默认 product.dqProduct） */
    dqProductForMatch?: {
      unitPrice?: any;
      unitPriceResult?: string | null;
      basicMotorCode?: string | null;
    } | null;
  },
) => {
  const dqForMatch = options?.dqProductForMatch ?? product.dqProduct;
  const unitPriceResult =
    options?.savedUnitPriceResult ??
    dqForMatch?.unitPriceResult ??
    product.unitPriceResult;
  if (isMotorUnitPriceNeedQuotation(unitPriceResult)) {
    const formPrice = options?.formUnitPrice;
    if (formPrice !== undefined && formPrice !== null && formPrice !== '') {
      return formPrice;
    }
    const saved = options?.savedUnitPrice ?? dqForMatch?.unitPrice;
    if (
      saved !== undefined &&
      saved !== null &&
      saved !== '' &&
      isDqBasicMotorCodeMatched(product, dqForMatch)
    ) {
      return saved;
    }
    return product.unitPrice;
  }
  // 系统匹配价：以 swap 后选型面价为准（境界产品 = 原始 jinUnitPrice）
  return product.unitPrice ?? options?.formUnitPrice ?? options?.savedUnitPrice ?? dqForMatch?.unitPrice;
};
